import clsx from 'clsx';
import { motion } from 'framer-motion';
import { Activity, Brain, Zap, ArrowDown } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';

// ── Config ────────────────────────────────────────────────────────────────────

const MODULE_META = {
  diamondedge:   { label: 'DiamondEdge', tag: 'MLB',  leagueId: 'mlb',  color: 'text-brand',       hexColor: '#3b82f6', bg: 'bg-brand/10 border-brand/20',            agentCount: 9 },
  courtedge:     { label: 'CourtEdge',   tag: 'WNBA', leagueId: 'wnba', color: 'text-warn',         hexColor: '#f59e0b', bg: 'bg-warn/10 border-warn/20',              agentCount: 8 },
  'courtedge-nba': { label: 'CourtEdge', tag: 'NBA',  leagueId: 'nba',  color: 'text-emerald-400', hexColor: '#10b981', bg: 'bg-emerald-500/10 border-emerald-500/20', agentCount: 8 },
} as const;

// ── FlowNode ──────────────────────────────────────────────────────────────────

interface FlowNodeProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  tag: string;
  hexColor: string;
  bg: string;
  stats: { label: string; value: string; highlight?: boolean }[];
  active: boolean;
}

function FlowNode({ icon: Icon, label, tag, hexColor, bg, stats, active }: FlowNodeProps) {
  return (
    <motion.div
      layout
      className={clsx(
        'rounded-lg border px-2.5 py-2 transition-all duration-300',
        active ? `${bg}` : 'border-surface-4 bg-surface-1/40',
      )}
      style={active ? { borderColor: `${hexColor}25` } : undefined}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <div
          className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${hexColor}20`, border: `1px solid ${hexColor}30` }}
        >
          <Icon className="w-3 h-3" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold leading-tight truncate" style={{ color: active ? hexColor : undefined }}>
            {label}
          </p>
          <p className="text-[9px] text-gray-700 leading-tight">{tag}</p>
        </div>
        {active && (
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ backgroundColor: hexColor }} />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: hexColor }} />
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {stats.map(({ label: sl, value, highlight }) => (
          <div key={sl} className="flex-1 text-center">
            <p className={clsx('text-xs font-bold font-mono tabular-nums leading-none', highlight ? 'text-gold' : active ? 'text-gray-200' : 'text-gray-600')}>
              {value}
            </p>
            <p className="text-[9px] text-gray-700 uppercase tracking-wide leading-none mt-0.5">{sl}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// ── Flow arrow ────────────────────────────────────────────────────────────────

function FlowArrow({ active }: { active: boolean }) {
  return (
    <div className="flex items-center justify-center py-0.5">
      <ArrowDown className={clsx('w-3 h-3 transition-colors', active ? 'text-purple/60' : 'text-surface-4')} />
    </div>
  );
}

// ── PulseUplink ───────────────────────────────────────────────────────────────

interface PulseUplinkProps {
  module: keyof typeof MODULE_META;
  recCount?: number;
  betNowCount?: number;
  className?: string;
}

export function PulseUplink({ module, recCount = 0, betNowCount = 0, className }: PulseUplinkProps) {
  const { agentActivity, signals } = useAppStore();
  const meta = MODULE_META[module];

  const activeAgents = new Set(
    agentActivity
      .filter((e) => !e.leagueId || e.leagueId === meta.leagueId)
      .filter((e) => Date.now() - new Date(e.timestamp).getTime() < 5 * 60_000)
      .map((e) => e.agent),
  ).size;

  const moduleSignals = signals.filter((s) => s.isActive && (!s.leagueId || s.leagueId === meta.leagueId)).length;

  const hasActivity = recCount > 0 || activeAgents > 0;

  return (
    <div className={clsx('flex flex-col h-full border-l border-surface-4 bg-surface-0/50', className)}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-surface-4 bg-surface-1/30 flex-shrink-0">
        <Zap className="w-3.5 h-3.5 text-purple-bright flex-shrink-0" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 flex-1">Pulse Uplink</p>
        <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple opacity-60" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple" />
        </span>
      </div>

      {/* Flow nodes */}
      <div className="flex-1 overflow-y-auto p-2.5 space-y-0">
        <p className="text-[9px] text-gray-700 uppercase tracking-widest text-center pb-2">Intelligence Pipeline</p>

        <FlowNode
          icon={Activity}
          label={meta.label}
          tag={meta.tag}
          hexColor={meta.hexColor}
          bg={meta.bg}
          stats={[
            { label: 'Recs', value: String(recCount) },
            { label: 'Signals', value: String(moduleSignals) },
          ]}
          active={recCount > 0}
        />

        <FlowArrow active={hasActivity} />

        <FlowNode
          icon={Brain}
          label="Agent Ensemble"
          tag={`${meta.agentCount} AGENTS`}
          hexColor="#a78bfa"
          bg="bg-purple/10 border-purple/20"
          stats={[
            { label: 'Active', value: String(activeAgents) },
          ]}
          active={activeAgents > 0}
        />

        <FlowArrow active={hasActivity} />

        <FlowNode
          icon={Zap}
          label="Pulse Command"
          tag="SYNTHESIS"
          hexColor="#d97706"
          bg="bg-gold/10 border-gold/20"
          stats={[
            { label: 'Opportunities', value: String(recCount) },
            { label: 'BET NOW', value: String(betNowCount), highlight: betNowCount > 0 },
          ]}
          active={recCount > 0}
        />
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-surface-4 flex-shrink-0 bg-surface-0/20">
        <div className="flex items-center justify-between">
          <span className="text-[9px] text-gray-700 uppercase tracking-widest">Module → Pulse</span>
          {hasActivity && (
            <span className="text-[9px] font-mono text-purple tabular-nums">Active</span>
          )}
        </div>
      </div>
    </div>
  );
}
